// src/services/sportsdata-service.js
// COMPLETE WORKING VERSION - NFL + College Football + MLB + NBA + NHL
// 🔥 ENHANCED with Multi-League Support
import { logger } from "../utils/logger";
import {
  getFootballSeasonYear,
  getBaseballSeasonYear,
  getBasketballHockeySeasonYear,
  getSeasonYearForLeague,
} from "../../utils/season";
class SportsDataService {
  constructor() {
    // Base URLs for all supported leagues
    this.nflBaseURL = 'https://api.sportsdata.io/v3/nfl/scores/json';
    this.cfbBaseURL = 'https://api.sportsdata.io/v3/cfb/scores/json';
    this.mlbBaseURL = 'https://api.sportsdata.io/v3/mlb/scores/json';
    this.nbaBaseURL = 'https://api.sportsdata.io/v3/nba/scores/json';
    this.nhlBaseURL = 'https://api.sportsdata.io/v3/nhl/scores/json';

    // Proxy base (Development API server) to hide keys
    this.proxyBase = 'http://localhost:3001/api/sportsdata-proxy';

    logger.sports('🏈 SportsData.io Service initialized (multi-league proxy mode)');
  }

  // =====================================================
  // UTILITY METHODS
  // =====================================================

  async makeRequest(url, apiKey, description = '') {

    try {
      // Determine league from URL
      let league = 'nfl'; // default
      if (url.includes('/cfb/')) league = 'cfb';
      else if (url.includes('/mlb/')) league = 'mlb';
      else if (url.includes('/nba/')) league = 'nba';
      else if (url.includes('/nhl/')) league = 'nhl';
      
      const proxied = `${this.proxyBase}?league=${encodeURIComponent(league)}&url=${encodeURIComponent(url)}`;
      const response = await fetch(proxied, { headers: { 'Accept': 'application/json' } });

      if (!response.ok) {
        const errorText = await response.text();
        logger.error(` API Error (${response.status}):`, errorText);
        // If the proxy reports missing API key, throw a typed error we can fallback on
        if (errorText && /Missing SportsData API key/i.test(errorText)) {
          const err = new Error('MISSING_SPORTSDATA_KEY');
          err.code = 'MISSING_SPORTSDATA_KEY';
          throw err;
        }

        if (response.status === 401) {
          throw new Error(`Authentication failed - check API key`);
        } else if (response.status === 403) {
          throw new Error(`Access forbidden - check subscription`);
        } else if (response.status === 429) {
          throw new Error(`Rate limit exceeded`);
        } else {
          throw new Error(`API error: ${response.status} - ${errorText}`);
        }
      }

      const data = await response.json();
      return data;
      
    } catch (error) {
      logger.error(` Request failed:`, error);
      throw error;
    }
  }

  // =====================================================
  // 🔥 ENHANCED NFL METHODS
  // =====================================================

  async getNFLTeams() {
    const url = `${this.nflBaseURL}/Teams`;
    return await this.makeRequest(url, this.nflApiKey, 'NFL Teams');
  }

  // 🔥 ENHANCED: NFL Schedule with Multiple Endpoints & Fallback (with ESPN fallback)
  async getNFLSchedule(teamKey, season = getFootballSeasonYear()) {
logger.info(`🏈 Getting NFL schedule for ${teamKey}`);
    
    try {
      // 🔥 TRY MULTIPLE ENDPOINTS
      let data = null;
      
      // Method 1: Try SchedulesBasic first
      try {
        const basicUrl = `${this.nflBaseURL}/SchedulesBasic/${season}`;
        data = await this.makeRequest(basicUrl, this.nflApiKey, `NFL SchedulesBasic ${season}`);
      } catch (basicError) {
        logger.warn(` SchedulesBasic failed, trying Schedules: ${basicError.message}`);
        
        // Method 2: Try regular Schedules endpoint
        try {
          const scheduleUrl = `${this.nflBaseURL}/Schedules/${season}`;
          data = await this.makeRequest(scheduleUrl, this.nflApiKey, `NFL Schedules ${season}`);
        } catch (scheduleError) {
          logger.warn(` Regular Schedules failed: ${scheduleError.message}`);
          
          // Method 3: Try team-specific endpoint
          const teamUrl = `${this.nflBaseURL}/Games/${season}/${teamKey}`;
          logger.debug(`🔗 Trying team-specific: ${teamUrl}`);
          data = await this.makeRequest(teamUrl, this.nflApiKey, `NFL Team Games ${teamKey}`);
        }
      }
      
      if (data && Array.isArray(data) && data.length > 0) {
        logger.success(` Raw NFL data received: ${data.length} total games`);
        
        // 🔥 ENHANCED FILTERING - More flexible team matching
        const teamGames = data.filter(game => {
          const homeMatch = game.HomeTeam === teamKey || 
                           game.HomeTeamName?.includes(teamKey) ||
                           game.home_team === teamKey;
          const awayMatch = game.AwayTeam === teamKey || 
                           game.AwayTeamName?.includes(teamKey) ||
                           game.away_team === teamKey;
          
          return homeMatch || awayMatch;
        });
        
        logger.debug(`🎯 Filtered games for ${teamKey}: ${teamGames.length} games`);
        
        if (teamGames.length > 0) {
          logger.success(` Found ${teamGames.length} NFL games for ${teamKey}`);
          logger.debug(' Sample game data:', teamGames[0]);
          
          const parsedGames = this.parseNFLGames(teamGames, teamKey, season);
          logger.debug(` Parsed ${parsedGames.length} NFL games`);
          return parsedGames;
        } else {
          logger.warn(` No games found for team ${teamKey} in ${data.length} total games`);
          logger.debug(' Available teams in data:', 
            [...new Set(data.map(g => g.HomeTeam || g.home_team).filter(Boolean))].slice(0, 10)
          );
        }
      } else {
        logger.warn(` No valid data received. Data type: ${typeof data}, Length: ${data?.length}`);
      }
      
    } catch (error) {
      logger.error(`❌ NFL Schedule failed for ${teamKey}:`, error);
      // ESPN FALLBACK if SportsData key is missing
      if (error?.code === 'MISSING_SPORTSDATA_KEY') {
        try {
          logger.warn('⚠️ Falling back to ESPN proxy for NFL schedule (no SportsData key)');
          const espnRes = await fetch(`/api/espn-proxy?type=nfl&teamName=${encodeURIComponent(this.getTeamNameFromNFLKey?.(teamKey) || teamKey)}&season=${season}`);
          if (espnRes.ok) {
            const json = await espnRes.json();
            const schedule = this.parseESPNLikeNFLSchedule(json, teamKey, season);
            if (Array.isArray(schedule) && schedule.length) return schedule;
          }
        } catch (e) {
          logger.warn('ESPN fallback failed:', e?.message || e);
        }
      }
      console.error('Error details:', {
        name: error.name,
        message: error.message,
        status: error.status || 'N/A'
      });
    }
    
    // 🔥 FALLBACK: Return a basic schedule structure
    logger.debug(` Generating fallback schedule for ${teamKey}`);
    return this.generateFallbackNFLSchedule(teamKey, season);
  }

  // Map NFL key back to human team name (simple common mapping)
  getTeamNameFromNFLKey(nflKey) {
    const map = {
      GB: 'Green Bay Packers', KC: 'Kansas City Chiefs', NE: 'New England Patriots', DAL: 'Dallas Cowboys', SF: 'San Francisco 49ers',
      PIT: 'Pittsburgh Steelers', CHI: 'Chicago Bears', PHI: 'Philadelphia Eagles', NYG: 'New York Giants', NYJ: 'New York Jets',
      MIA: 'Miami Dolphins', BUF: 'Buffalo Bills', BAL: 'Baltimore Ravens', CIN: 'Cincinnati Bengals', CLE: 'Cleveland Browns',
      HOU: 'Houston Texans', IND: 'Indianapolis Colts', JAC: 'Jacksonville Jaguars', TEN: 'Tennessee Titans',
      DEN: 'Denver Broncos', LV: 'Las Vegas Raiders', LAC: 'Los Angeles Chargers', LAR: 'Los Angeles Rams',
      ARI: 'Arizona Cardinals', SEA: 'Seattle Seahawks', MIN: 'Minnesota Vikings', DET: 'Detroit Lions',
      ATL: 'Atlanta Falcons', CAR: 'Carolina Panthers', NO: 'New Orleans Saints', TB: 'Tampa Bay Buccaneers',
      WAS: 'Washington Commanders'
    };
    return map[nflKey] || nflKey;
  }

  // Parse ESPN-style schedule into our NFL game shape
  parseESPNLikeNFLSchedule(json, teamKey, season) {
    try {
      const events = Array.isArray(json?.events) ? json.events : [];
      const self = json?.team?.displayName || this.getTeamNameFromNFLKey(teamKey);
      return events.map((event, index) => {
        const comp = event?.competitions?.[0] || {};
        const home = comp?.competitors?.find(c => c.homeAway === 'home');
        const away = comp?.competitors?.find(c => c.homeAway === 'away');
        const homeName = home?.team?.displayName;
        const awayName = away?.team?.displayName;
        const isHomeGame = homeName === self;
        const opponent = isHomeGame ? (awayName || 'TBD') : (homeName || 'TBD');
        const gameDate = event?.date ? new Date(event.date) : new Date(season, 8, (index + 1) * 7);
        const isCompleted = (event?.status?.type?.state || '').toLowerCase() === 'post'
          || (event?.status?.type?.completed === true);
        return {
          id: String(event?.id || `espn_${teamKey}_${index}`),
          date: gameDate,
          opponent,
          opponentId: opponent,
          isHomeGame,
          venue: comp?.venue?.fullName || (isHomeGame ? 'Home Stadium' : `@${opponent}`),
          status: event?.status?.type?.description || 'Scheduled',
          isCompleted,
          homeScore: home?.score ? Number(home.score) : null,
          awayScore: away?.score ? Number(away.score) : null,
          gameResult: null,
          week: event?.week?.number || index + 1,
          time: gameDate,
          broadcast: comp?.broadcasts?.[0]?.names?.[0] || null,
          season,
          dataSource: 'ESPN (fallback)'
        };
      });
    } catch {
      return [];
    }
  }

  // 🔥 NEW: Fallback schedule generator
  generateFallbackNFLSchedule(teamKey, season) {
    logger.debug(` Generating fallback NFL schedule for ${teamKey}`);
    
    // Basic 17-game NFL schedule structure
    const fallbackGames = [];
    
    for (let week = 1; week <= 17; week++) {
      const gameDate = new Date(season, 8, week * 7); // Start in September
      
      fallbackGames.push({
        id: `${teamKey}_week_${week}_${season}`,
        date: gameDate,
        opponent: 'TBD',
        opponentId: 'TBD',
        isHomeGame: week % 2 === 0,
        venue: week % 2 === 0 ? 'Home Stadium' : 'Away',
        status: 'Scheduled',
        isCompleted: false,
        homeScore: null,
        awayScore: null,
        gameResult: null,
        week: week,
        time: gameDate,
        broadcast: null,
        season: season,
        dataSource: 'SportsData.io NFL Fallback',
        isFallback: true
      });
    }
    
    logger.success(` Generated ${fallbackGames.length} fallback games`);
    return fallbackGames;
  }

  async getNFLRoster(teamKey, season = getFootballSeasonYear()) {
    logger.debug(`👥 Getting NFL roster for ${teamKey} - ${season}`);
    
    try {
      const url = `${this.nflBaseURL}/Players/${teamKey}`;
      const data = await this.makeRequest(url, this.nflApiKey, `NFL Players ${teamKey}`);
      
      if (data && Array.isArray(data) && data.length > 0) {
        logger.success(` Found ${data.length} NFL players for ${teamKey}`);
        return this.parseNFLRoster(data, teamKey);
      }
    } catch (error) {
      logger.error(` NFL Roster failed: ${error.message}`);
    }
    
    logger.warn(` No NFL roster found for ${teamKey} ${season}`);
    return [];
  }

  // 🔥 ENHANCED: NFL Games Parser
  parseNFLGames(games, teamKey, season = getFootballSeasonYear()) {
    logger.debug(` Parsing ${games.length} NFL games for ${teamKey}`);
    
    return games.map((game, index) => {
      try {
        // 🔥 ENHANCED PARSING - Handle multiple data formats
        const homeTeam = game.HomeTeam || game.home_team || game.HomeTeamName;
        const awayTeam = game.AwayTeam || game.away_team || game.AwayTeamName;
        
        const isHomeGame = homeTeam === teamKey;
        const opponent = isHomeGame ? awayTeam : homeTeam;
        
        // 🔥 ENHANCED DATE PARSING
        let gameDate;
        if (game.DateTime) {
          gameDate = new Date(game.DateTime);
        } else if (game.Date) {
          gameDate = new Date(game.Date);
        } else if (game.start_time) {
          gameDate = new Date(game.start_time);
        } else {
          // Fallback: Use week-based estimation
          const weekNum = game.Week || game.week || (index + 1);
          gameDate = new Date(season, 8, weekNum * 7); // September start
        }
        
        const isCompleted = game.Status === 'Final' || 
                            game.IsClosed === true || 
                            game.status === 'completed';
        
        let gameResult = null;
        if (isCompleted && game.HomeScore !== null && game.AwayScore !== null) {
          const teamScore = isHomeGame ? game.HomeScore : game.AwayScore;
          const opponentScore = isHomeGame ? game.AwayScore : game.HomeScore;
          
          if (teamScore > opponentScore) gameResult = 'W';
          else if (teamScore < opponentScore) gameResult = 'L';
          else gameResult = 'T';
        }

        const parsedGame = {
          id: String(game.GameID || game.id || `nfl_${teamKey}_${index}_${game.Week || index + 1}`),
          date: gameDate,
          opponent: opponent,
          opponentId: opponent,
          isHomeGame: isHomeGame,
          venue: game.StadiumDetails?.Name || (isHomeGame ? 'Home Stadium' : `@${opponent}`),
          status: game.Status || game.status || 'Scheduled',
          isCompleted: isCompleted,
          homeScore: game.HomeScore || null,
          awayScore: game.AwayScore || null,
          gameResult: gameResult,
          week: game.Week || game.week || (index + 1),
          time: gameDate,
          broadcast: game.Channel || null,
          season: game.Season || season,
          dataSource: 'SportsData.io NFL',
          // Add debugging info
          _originalData: {
            HomeTeam: game.HomeTeam,
            AwayTeam: game.AwayTeam,
            DateTime: game.DateTime,
            Week: game.Week
          }
        };
        
        logger.success(` Parsed NFL game ${index + 1}: Week ${parsedGame.week} - ${isHomeGame ? 'vs' : '@'} ${parsedGame.opponent}`);
        return parsedGame;
        
      } catch (error) {
        logger.error(` Failed to parse NFL game ${index}:`, error, game);
        
        // Return a basic fallback game
        return {
          id: `nfl_fallback_${teamKey}_${index}`,
          date: new Date(season, 8, (index + 1) * 7),
          opponent: 'TBD',
          opponentId: 'TBD',
          isHomeGame: true,
          venue: 'Home Stadium',
          status: 'Scheduled',
          isCompleted: false,
          homeScore: null,
          awayScore: null,
          gameResult: null,
          week: index + 1,
          time: new Date(season, 8, (index + 1) * 7),
          broadcast: null,
          season: season,
          dataSource: 'SportsData.io NFL Fallback',
          isFallback: true,
          _error: error.message
        };
      }
    }).filter(game => game !== null);
  }

  parseNFLRoster(players, teamKey) {
  const parseHeight = (heightInches) => {
    if (!heightInches || isNaN(heightInches) || heightInches <= 0) {
      return 'N/A';
    }
    const feet = Math.floor(heightInches / 12);
    const inches = heightInches % 12;
    return `${feet}'${inches}"`;
  };

  return players.map((player, index) => {
    try {
      return {
        id: String(player.PlayerID || `nfl-${index}`),
        name: `${player.FirstName || ''} ${player.LastName || ''}`.trim() || 'Unknown Player',
        position: player.Position || 'N/A',
        jersey: String(player.Number || player.Jersey || 'N/A'),
        height: parseHeight(player.Height),
        weight: player.Weight ? `${player.Weight} lbs` : 'N/A',
        experience: player.Experience ? `${player.Experience} years` : 'Rookie',
        college: player.College || 'N/A',
        age: player.Age || 'N/A',
        dataSource: 'SportsData.io NFL',
        team: teamKey
      };
    } catch (error) {
      logger.error(` Error parsing NFL player ${index}:`, error);
      return null;
    }
  }).filter(player => player !== null);
}

  // =====================================================
  // COLLEGE FOOTBALL METHODS  
  // =====================================================

  async getCollegeTeams() {
    const url = `${this.cfbBaseURL}/Teams`;
    return await this.makeRequest(url, this.cfbApiKey, 'College Teams');
  }

  async getCollegeSchedule(teamKey, season = getFootballSeasonYear()) {
    logger.debug(`🏫 Getting College schedule for ${teamKey} - ${season}`);
    
    const endpoints = [
      `/SchedulesBasic/${season}`,
      `/Schedules/${season}`,
      `/Games/${season}`
    ];

    for (const endpoint of endpoints) {
      try {
        const url = `${this.cfbBaseURL}${endpoint}`;
        const data = await this.makeRequest(url, this.cfbApiKey, `College ${endpoint}`);
        
        if (data && Array.isArray(data)) {
          const teamGames = data.filter(game => 
            game.HomeTeam === teamKey || game.AwayTeam === teamKey
          );
          
          if (teamGames.length > 0) {
            logger.success(` Found ${teamGames.length} College games for ${teamKey}`);
            return this.parseCollegeGames(teamGames, teamKey, season);
          }
        }
      } catch (error) {
        logger.warn(` ${endpoint} failed, trying next...`);
      }
    }
    
    logger.warn(` No College schedule found for ${teamKey} ${season}`);
    return [];
  }

  async getCollegeRoster(teamKey, season = getFootballSeasonYear()) {
    logger.debug(`👥 Getting College roster for ${teamKey} - ${season}`);
    
    const endpoints = [
      `/Players/${teamKey}`,
      `/PlayersBasic/${teamKey}`
    ];

    for (const endpoint of endpoints) {
      try {
        const url = `${this.cfbBaseURL}${endpoint}`;
        const data = await this.makeRequest(url, this.cfbApiKey, `College ${endpoint}`);
        
        if (data && Array.isArray(data) && data.length > 0) {
          logger.success(` Found ${data.length} College players for ${teamKey}`);
          return this.parseCollegeRoster(data, teamKey);
        }
      } catch (error) {
        logger.warn(` ${endpoint} failed, trying next...`);
      }
    }
    
    logger.warn(` No College roster found for ${teamKey} ${season}`);
    return [];
  }

  parseCollegeGames(games, teamKey, season = getFootballSeasonYear()) {
    return games.map((game, index) => {
      try {
        const isHomeGame = game.HomeTeam === teamKey;
        const opponent = isHomeGame ? game.AwayTeam : game.HomeTeam;
        const gameDate = game.DateTime ? new Date(game.DateTime) : new Date(game.Date);
        
        const isCompleted = game.Status === 'Final' || game.IsClosed === true;
        let gameResult = null;
        
        if (isCompleted && game.HomeScore !== null && game.AwayScore !== null) {
          const teamScore = isHomeGame ? game.HomeScore : game.AwayScore;
          const opponentScore = isHomeGame ? game.AwayScore : game.HomeScore;
          
          if (teamScore > opponentScore) gameResult = 'W';
          else if (teamScore < opponentScore) gameResult = 'L';
          else gameResult = 'T';
        }

        return {
          id: String(game.GameID || game.ScoreID || `cfb-${index}`),
          date: gameDate,
          opponent: opponent,
          opponentId: opponent,
          isHomeGame: isHomeGame,
          venue: game.StadiumDetails?.Name || (isHomeGame ? 'Home Stadium' : 'Away'),
          status: game.Status || (isCompleted ? 'Final' : 'Scheduled'),
          isCompleted: isCompleted,
          homeScore: game.HomeScore,
          awayScore: game.AwayScore,
          gameResult: gameResult,
          week: game.Week || null,
          time: gameDate,
          broadcast: game.Channel || null,
          season: game.Season || season,
          dataSource: 'SportsData.io College'
        };
      } catch (error) {
        logger.error(` Error parsing College game ${index}:`, error);
        return null;
      }
    }).filter(game => game !== null);
  }

  parseCollegeRoster(players, teamKey) {
    return players.map((player, index) => {
      try {
        return {
          id: String(player.PlayerID || `cfb-${index}`),
          name: `${player.FirstName || ''} ${player.LastName || ''}`.trim() || 'Unknown Player',
          position: player.Position || 'N/A',
          jersey: String(player.Number || player.Jersey || 'N/A'),
          year: player.Class || 'N/A',
          height: player.Height ? `${Math.floor(player.Height / 12)}'${player.Height % 12}"` : 'N/A',
          weight: player.Weight ? `${player.Weight} lbs` : 'N/A',
          hometown: player.Hometown || 'N/A',
          dataSource: 'SportsData.io College',
          team: teamKey
        };
      } catch (error) {
        logger.error(` Error parsing College player ${index}:`, error);
        return null;
      }
    }).filter(player => player !== null);
  }

  // =====================================================
  // TEAM MAPPING - EXACT MAPPINGS THAT WORKED BEFORE
  // =====================================================

  getNFLTeamKeyFromName(teamName) {
    const nflMapping = {
      // AFC East
      'Buffalo Bills': 'BUF',
      'Miami Dolphins': 'MIA',
      'New England Patriots': 'NE',
      'New York Jets': 'NYJ',
      
      // AFC North
      'Baltimore Ravens': 'BAL',
      'Cincinnati Bengals': 'CIN',
      'Cleveland Browns': 'CLE',
      'Pittsburgh Steelers': 'PIT',
      
      // AFC South
      'Houston Texans': 'HOU',
      'Indianapolis Colts': 'IND',
      'Jacksonville Jaguars': 'JAC',
      'Tennessee Titans': 'TEN',
      
      // AFC West
      'Denver Broncos': 'DEN',
      'Kansas City Chiefs': 'KC',
      'Las Vegas Raiders': 'LV',
      'Los Angeles Chargers': 'LAC',
      
      // NFC East
      'Dallas Cowboys': 'DAL',
      'New York Giants': 'NYG',
      'Philadelphia Eagles': 'PHI',
      'Washington Commanders': 'WAS',
      
      // NFC North
      'Chicago Bears': 'CHI',
      'Detroit Lions': 'DET',
      'Green Bay Packers': 'GB',  // ✅ This fixes the Packers
      'Minnesota Vikings': 'MIN',
      
      // NFC South
      'Atlanta Falcons': 'ATL',
      'Carolina Panthers': 'CAR',
      'New Orleans Saints': 'NO',
      'Tampa Bay Buccaneers': 'TB',
      
      // NFC West
      'Arizona Cardinals': 'ARI',
      'Los Angeles Rams': 'LAR',
      'San Francisco 49ers': 'SF',
      'Seattle Seahawks': 'SEA'
    };
    
    const teamKey = nflMapping[teamName];
    if (!teamKey) {
      console.warn(`⚠️ NFL team key not found for: ${teamName}`);
      return teamName.split(' ').pop().substring(0, 3).toUpperCase();
    }
    
    logger.debug(`🔍 NFL Team Mapping: ${teamName} → ${teamKey}`);
    return teamKey;
  }

  getCollegeTeamKeyFromName(teamName) {
    // ✅ USING EXACT MAPPINGS THAT WORKED BEFORE
  const teamMappings = {
      // SEC Conference
      'Alabama': 'ALA',
      'Auburn': 'AUBRN',
      'Arkansas': 'ARK',
      'Florida': 'FL',
      'Georgia': 'GA',
      'Kentucky': 'UK',
      'LSU': 'LSU',
      'Mississippi State': 'MSPST',
      'Missouri': 'MISSR',
      'Ole Miss': 'MISS',
      'South Carolina': 'SC',
      'Tennessee': 'TENN',
      'Texas A&M': 'TXAM',
      'Vanderbilt': 'VAND',
      'Texas': 'TX',
      'Oklahoma': 'OKL',

      // Big Ten Conference
      'Wisconsin': 'WISC',
      'Michigan': 'MICH',
      'Ohio State': 'OHIOST',
      'Penn State': 'PENNST',
      'Michigan State': 'MST',
      'Iowa': 'IOWA',
      'Minnesota': 'MINNST',
      'Illinois': 'ILL',
      'Indiana': 'IND',
      'Maryland': 'MARY',
      'Nebraska': 'NEBR',
      'Northwestern': 'NW',
      'Purdue': 'PUR',
      'Rutgers': 'RUTGER',
      'Oregon': 'ORE',
      'USC': 'USC',
      'UCLA': 'UCLA',
      'Washington': 'WASH',

      // ACC Conference
      'Boston College': 'BOSCOL',
      'Clemson': 'CLMSN',
      'Duke': 'DUKE',
      'Florida State': 'FLST',
      'Georgia Tech': 'GTECH',
      'Louisville': 'LOU',
      'Miami': 'MIA',
      'North Carolina': 'NCAR',
      'NC State': 'NCST',
      'Pittsburgh': 'PITT',
      'Syracuse': 'SYRA',
      'Virginia': 'VIR',
      'Virginia Tech': 'VTECH',
      'Wake Forest': 'WAKE',
      'California': 'CAH',
      'Stanford': 'STAN',
      'SMU': 'SMU',

      // Big 12 Conference
      'Arizona': 'ARZ',
      'Arizona State': 'ARZST',
      'Baylor': 'BAYL',
      'BYU': 'BYU',
      'Cincinnati': 'CIN',
      'Colorado': 'COL',
      'Houston': 'HOU',
      'Iowa State': 'IOWAST',
      'Kansas': 'KAN',
      'Kansas State': 'KANST',
      'Oklahoma State': 'OKST',
      'TCU': 'TCU',
      'Texas Tech': 'TXTECH',
      'UCF': 'UCF',
      'Utah': 'UTAH',
      'West Virginia': 'WVIR',

      // Mountain West Conference
      'Air Force': 'AIRF',
      'Boise State': 'BOISE',
      'Colorado State': 'COLST',
      'Fresno State': 'FREST',
      'Hawaii': 'HAWAII',
      'Nevada': 'NEVADA',
      'New Mexico': 'NMX',
      'San Diego State': 'SDST',
      'San Jose State': 'SJST',
      'UNLV': 'UNLV',
      'Utah State': 'UTAHST',
      'Wyoming': 'WYOM',

      // American Athletic Conference
      'Army': 'ARMY',
      'Charlotte': 'CHARLT',
      'East Carolina': 'ECAR',
      'Florida Atlantic': 'FLATL',
      'Memphis': 'MPHS',
      'Navy': 'NAVY',
      'North Texas': 'NTX',
      'Rice': 'RICE',
      'South Florida': 'SFL',
      'Temple': 'TEMPL',
      'Tulane': 'TULANE',
      'Tulsa': 'TULSA',
      'UAB': 'UAB',
      'UTSA': 'UTSA',

      // Conference USA
      'Florida International': 'FLINT',
      'Jacksonville State': 'JAXST',
      'Kennesaw State': 'KENEST',
      'Liberty': 'LIBRTY',
      'Louisiana Tech': 'LOUTCH',
      'Middle Tennessee': 'MTNST',
      'New Mexico State': 'NMXST',
      'Sam Houston': 'SMHO',
      'Southern Miss': 'SOUMIS',
      'UTEP': 'UTEP',
      'Western Kentucky': 'WKENT',

      // Mid-American Conference
      'Akron': 'AKRON',
      'Ball State': 'BALLST',
      'Bowling Green': 'BOWLGR',
      'Buffalo': 'BUF',
      'Central Michigan': 'CMICH',
      'Eastern Michigan': 'EMICH',
      'Kent State': 'KENTST',
      'Miami (OH)': 'MIAOH',
      'Northern Illinois': 'NILL',
      'Ohio': 'OHIO',
      'Toledo': 'TOLEDO',
      'Western Michigan': 'WMICH',

      // Sun Belt Conference - East
      'Appalachian State': 'APPLST',
      'Coastal Carolina': 'COAST',
      'Georgia Southern': 'GAS',
      'Georgia State': 'GAST',
      'James Madison': 'JMAD',
      'Marshall': 'MARSH',
      'Old Dominion': 'OLD',

      // Sun Belt Conference - West
      'Arkansas State': 'ARKST',
      'Louisiana': 'LOULAF',
      'Louisiana Monroe': 'LAMON',
      'South Alabama': 'SALA',
      'Southern Miss': 'SOUMIS',
      'Texas State': 'TXST',
      'Troy': 'TROY',

      // Pac-12 Conference
      'Oregon State': 'OREGST',
      'Washington State': 'WASHST',

      // FBS Independents
      'Connecticut': 'UCONN',
      'Massachusetts': 'UMASS',
      'Notre Dame': 'ND'
    };
    
    return teamMappings[teamName] || teamName.toUpperCase().substring(0, 4);
  }

  // ✅ REVERSE MAPPING - Convert SportsData.io Key back to readable team name
  getTeamNameFromKey(teamKey) {
    const keyToNameMappings = {
      // SEC Conference
      'ALA': 'Alabama',
      'AUBRN': 'Auburn',
      'ARK': 'Arkansas',
      'FL': 'Florida',
      'GA': 'Georgia',
      'UK': 'Kentucky',
      'LSU': 'LSU',
      'MSPST': 'Mississippi State',
      'MISSR': 'Missouri',
      'MISS': 'Ole Miss',
      'SC': 'South Carolina',
      'TENN': 'Tennessee',
      'TXAM': 'Texas A&M',
      'VAND': 'Vanderbilt',
      'TX': 'Texas',
      'OKL': 'Oklahoma',

      // Big Ten Conference
      'WISC': 'Wisconsin',
      'MICH': 'Michigan',
      'OHIOST': 'Ohio State',
      'PENNST': 'Penn State',
      'MST': 'Michigan State',
      'IOWA': 'Iowa',
      'MINNST': 'Minnesota',
      'ILL': 'Illinois',
      'IND': 'Indiana',
      'MARY': 'Maryland',
      'NEBR': 'Nebraska',
      'NW': 'Northwestern',
      'PUR': 'Purdue',
      'RUTGER': 'Rutgers',
      'ORE': 'Oregon',
      'USC': 'USC',
      'UCLA': 'UCLA',
      'WASH': 'Washington',

      // ACC Conference
      'BOSCOL': 'Boston College',
      'CLMSN': 'Clemson',
      'DUKE': 'Duke',
      'FLST': 'Florida State',
      'GTECH': 'Georgia Tech',
      'LOU': 'Louisville',
      'MIA': 'Miami',
      'NCAR': 'North Carolina',
      'NCST': 'NC State',
      'PITT': 'Pittsburgh',
      'SYRA': 'Syracuse',
      'VIR': 'Virginia',
      'VTECH': 'Virginia Tech',
      'WAKE': 'Wake Forest',
      'CAH': 'California',
      'STAN': 'Stanford',
      'SMU': 'SMU',

      // Add more as needed...
      'ND': 'Notre Dame',
      'ARMY': 'Army',
      'NAVY': 'Navy'
    };

    return keyToNameMappings[teamKey] || teamKey;
  }

  // =====================================================
  // UNIFIED INTERFACE METHODS
  // =====================================================

  async getTeamSchedule(teamName, league, season) {
    const resolvedSeason = season ?? getSeasonYearForLeague(league);
    logger.debug(`📅 Getting ${league} schedule for ${teamName} - ${resolvedSeason}`);
    
    if (league.toLowerCase() === 'nfl') {
      const teamKey = this.getNFLTeamKeyFromName(teamName);
      return await this.getNFLSchedule(teamKey, resolvedSeason);
    } else {
      const teamKey = this.getCollegeTeamKeyFromName(teamName);
      return await this.getCollegeSchedule(teamKey, resolvedSeason);
    }
  }

  async getTeamRoster(teamName, league, season = getFootballSeasonYear()) {
    logger.debug(`👥 Getting ${league} roster for ${teamName} - ${season}`);
    
    if (league.toLowerCase() === 'nfl') {
      const teamKey = this.getNFLTeamKeyFromName(teamName);
      return await this.getNFLRoster(teamKey, season);
    } else {
      const teamKey = this.getCollegeTeamKeyFromName(teamName);
      return await this.getCollegeRoster(teamKey, season);
    }
  }

  // =====================================================
  // 🔥 NEW: DEBUG METHODS
  // =====================================================

  // Debug function for NFL schedule issues
  async debugNFLSchedule(teamKey) {
    logger.debug('🔍 DEBUGGING NFL SCHEDULE FOR:', teamKey);
    
    try {
      // Test the service directly
      const schedule = await this.getNFLSchedule(teamKey);
      logger.debug('📊 Direct service result:', schedule);
      
      return { schedule, success: true };
      
    } catch (error) {
      console.error('❌ Debug failed:', error);
      return { error: error.message, success: false };
    }
  }

  // =====================================================
  // TEST METHODS
  // =====================================================

  // =====================================================
  // 🔥 NEW: MLB METHODS
  // =====================================================

  async getMLBTeams() {
    const url = `${this.mlbBaseURL}/Teams`;
    return await this.makeRequest(url, this.mlbApiKey, 'MLB Teams');
  }

  // Map MLB team names to SportsData.io abbreviations
  getMLBTeamKeyFromName(teamName) {
    const mlbMapping = {
      'Arizona Diamondbacks': 'ARI',
      'Atlanta Braves': 'ATL',
      'Baltimore Orioles': 'BAL',
      'Boston Red Sox': 'BOS',
      'Chicago Cubs': 'CHC',
      'Chicago White Sox': 'CWS',
      'Cincinnati Reds': 'CIN',
      'Cleveland Guardians': 'CLE',
      'Colorado Rockies': 'COL',
      'Detroit Tigers': 'DET',
      'Houston Astros': 'HOU',
      'Kansas City Royals': 'KC',
      'Los Angeles Angels': 'LAA',
      'Los Angeles Dodgers': 'LAD',
      'Miami Marlins': 'MIA',
      'Milwaukee Brewers': 'MIL',
      'Minnesota Twins': 'MIN',
      'New York Mets': 'NYM',
      'New York Yankees': 'NYY',
      'Oakland Athletics': 'OAK',
      'Philadelphia Phillies': 'PHI',
      'Pittsburgh Pirates': 'PIT',
      'San Diego Padres': 'SD',
      'San Francisco Giants': 'SF',
      'Seattle Mariners': 'SEA',
      'St. Louis Cardinals': 'STL',
      'Tampa Bay Rays': 'TB',
      'Texas Rangers': 'TEX',
      'Toronto Blue Jays': 'TOR',
      'Washington Nationals': 'WSH'
    };
    
    return mlbMapping[teamName] || teamName;
  }

  async getMLBSchedule(teamName, season = getBaseballSeasonYear()) {
    const teamKey = this.getMLBTeamKeyFromName(teamName);
    logger.info(`⚾ Getting MLB schedule for ${teamName} (${teamKey})`);
    
    try {
      let data = null;
      
      // Try team-specific endpoint first (most reliable)
      try {
        const teamUrl = `${this.mlbBaseURL}/Games/${season}/${teamKey}`;
        logger.debug(`🔗 Trying team-specific: ${teamUrl}`);
        data = await this.makeRequest(teamUrl, this.mlbApiKey, `MLB Team Games ${teamKey}`);
      } catch (teamError) {
        logger.warn(` Team-specific failed, trying SchedulesBasic: ${teamError.message}`);
        
        try {
          const basicUrl = `${this.mlbBaseURL}/SchedulesBasic/${season}`;
          data = await this.makeRequest(basicUrl, this.mlbApiKey, `MLB SchedulesBasic ${season}`);
        } catch (basicError) {
          logger.warn(` SchedulesBasic failed, trying Schedules: ${basicError.message}`);
          
          try {
            const scheduleUrl = `${this.mlbBaseURL}/Schedules/${season}`;
            data = await this.makeRequest(scheduleUrl, this.mlbApiKey, `MLB Schedules ${season}`);
          } catch (scheduleError) {
            logger.warn(` Regular Schedules failed: ${scheduleError.message}`);
            throw new Error('All MLB endpoints failed');
          }
        }
      }
      
      if (data && Array.isArray(data) && data.length > 0) {
        logger.success(` Raw MLB data received: ${data.length} total games`);
        
        const teamGames = data.filter(game => {
          const homeMatch = game.HomeTeam === teamKey || 
                           game.HomeTeamName?.includes(teamKey) ||
                           game.home_team === teamKey;
          const awayMatch = game.AwayTeam === teamKey || 
                           game.AwayTeamName?.includes(teamKey) ||
                           game.away_team === teamKey;
          
          return homeMatch || awayMatch;
        });
        
        logger.debug(`🎯 Filtered games for ${teamKey}: ${teamGames.length} games`);
        
        if (teamGames.length > 0) {
          logger.success(` Found ${teamGames.length} MLB games for ${teamKey}`);
          const parsedGames = this.parseMLBGames(teamGames, teamKey, season);
          logger.debug(` Parsed ${parsedGames.length} MLB games`);
          return parsedGames;
        }
      }
      
    } catch (error) {
      logger.error(`❌ MLB Schedule failed for ${teamKey}:`, error);
    }
    
    // Fallback: Return a basic schedule structure
    logger.debug(` Generating fallback MLB schedule for ${teamKey}`);
    return this.generateFallbackMLBSchedule(teamKey, season);
  }

  parseMLBGames(games, teamKey, season = getBaseballSeasonYear()) {
    logger.debug(` Parsing ${games.length} MLB games for ${teamKey}`);
    
    return games.map((game, index) => {
      try {
        const homeTeam = game.HomeTeam || game.home_team || game.HomeTeamName;
        const awayTeam = game.AwayTeam || game.away_team || game.AwayTeamName;
        
        const isHomeGame = homeTeam === teamKey;
        const opponent = isHomeGame ? awayTeam : homeTeam;
        
        let gameDate;
        if (game.DateTime) {
          gameDate = new Date(game.DateTime);
        } else if (game.Date) {
          gameDate = new Date(game.Date);
        } else {
          const gameNum = game.GameNumber || (index + 1);
          gameDate = new Date(season, 2, gameNum * 3); // March start
        }
        
        const isCompleted = game.Status === 'Final' || 
                            game.IsClosed === true || 
                            game.status === 'completed';
        
        let gameResult = null;
        if (isCompleted && game.HomeScore !== null && game.AwayScore !== null) {
          const teamScore = isHomeGame ? game.HomeScore : game.AwayScore;
          const opponentScore = isHomeGame ? game.AwayScore : game.HomeScore;
          
          if (teamScore > opponentScore) gameResult = 'W';
          else if (teamScore < opponentScore) gameResult = 'L';
          else gameResult = 'T';
        }

        return {
          id: String(game.GameID || game.id || `mlb_${teamKey}_${index}_${game.GameNumber || index + 1}`),
          date: gameDate,
          opponent: opponent,
          opponentId: opponent,
          isHomeGame: isHomeGame,
          venue: game.StadiumDetails?.Name || (isHomeGame ? 'Home Stadium' : `@${opponent}`),
          status: game.Status || game.status || 'Scheduled',
          isCompleted: isCompleted,
          homeScore: game.HomeScore || null,
          awayScore: game.AwayScore || null,
          gameResult: gameResult,
          week: game.GameNumber || (index + 1),
          time: gameDate,
          broadcast: game.Channel || null,
          season: game.Season || season,
          dataSource: 'SportsData.io MLB'
        };
        
      } catch (error) {
        logger.error(` Failed to parse MLB game ${index}:`, error);
        return {
          id: `mlb_${teamKey}_${index}_fallback`,
          date: new Date(season, 2, (index + 1) * 3),
          opponent: 'TBD',
          opponentId: 'TBD',
          isHomeGame: index % 2 === 0,
          venue: 'TBD',
          status: 'Scheduled',
          isCompleted: false,
          homeScore: null,
          awayScore: null,
          gameResult: null,
          week: index + 1,
          time: new Date(season, 2, (index + 1) * 3),
          broadcast: null,
          season: season,
          dataSource: 'MLB Fallback'
        };
      }
    });
  }

  generateFallbackMLBSchedule(teamKey, season) {
    logger.debug(` Generating fallback MLB schedule for ${teamKey}`);
    
    const fallbackGames = [];
    
    for (let game = 1; game <= 162; game++) {
      const gameDate = new Date(season, 2, game * 2); // March start, games every 2 days
      
      fallbackGames.push({
        id: `${teamKey}_game_${game}_${season}`,
        date: gameDate,
        opponent: 'TBD',
        opponentId: 'TBD',
        isHomeGame: game % 2 === 0,
        venue: game % 2 === 0 ? 'Home Stadium' : 'Away',
        status: 'Scheduled',
        isCompleted: false,
        homeScore: null,
        awayScore: null,
        gameResult: null,
        week: game,
        time: gameDate,
        broadcast: null,
        season: season,
        dataSource: 'SportsData.io MLB Fallback',
        isFallback: true
      });
    }
    
    logger.success(` Generated ${fallbackGames.length} fallback MLB games`);
    return fallbackGames;
  }

  // =====================================================
  // 🔥 NEW: NBA METHODS
  // =====================================================

  async getNBATeams() {
    const url = `${this.nbaBaseURL}/Teams`;
    return await this.makeRequest(url, this.nbaApiKey, 'NBA Teams');
  }

  // Map NBA team names to SportsData.io abbreviations
  getNBATeamKeyFromName(teamName) {
    const nbaMapping = {
      'Atlanta Hawks': 'ATL',
      'Boston Celtics': 'BOS',
      'Brooklyn Nets': 'BKN',
      'Charlotte Hornets': 'CHA',
      'Chicago Bulls': 'CHI',
      'Cleveland Cavaliers': 'CLE',
      'Dallas Mavericks': 'DAL',
      'Denver Nuggets': 'DEN',
      'Detroit Pistons': 'DET',
      'Golden State Warriors': 'GSW',
      'Houston Rockets': 'HOU',
      'Indiana Pacers': 'IND',
      'LA Clippers': 'LAC',
      'Los Angeles Lakers': 'LAL',
      'Memphis Grizzlies': 'MEM',
      'Miami Heat': 'MIA',
      'Milwaukee Bucks': 'MIL',
      'Minnesota Timberwolves': 'MIN',
      'New Orleans Pelicans': 'NOP',
      'New York Knicks': 'NYK',
      'Oklahoma City Thunder': 'OKC',
      'Orlando Magic': 'ORL',
      'Philadelphia 76ers': 'PHI',
      'Phoenix Suns': 'PHX',
      'Portland Trail Blazers': 'POR',
      'Sacramento Kings': 'SAC',
      'San Antonio Spurs': 'SAS',
      'Toronto Raptors': 'TOR',
      'Utah Jazz': 'UTA',
      'Washington Wizards': 'WAS'
    };
    
    return nbaMapping[teamName] || teamName;
  }

  async getNBASchedule(teamName, season = getBasketballHockeySeasonYear()) {
    const teamKey = this.getNBATeamKeyFromName(teamName);
    logger.info(`🏀 Getting NBA schedule for ${teamName} (${teamKey})`);
    
    try {
      let data = null;
      
      // Try team-specific endpoint first (most reliable)
      try {
        const teamUrl = `${this.nbaBaseURL}/Games/${season}/${teamKey}`;
        logger.debug(`🔗 Trying team-specific: ${teamUrl}`);
        data = await this.makeRequest(teamUrl, this.nbaApiKey, `NBA Team Games ${teamKey}`);
      } catch (teamError) {
        logger.warn(` Team-specific failed, trying SchedulesBasic: ${teamError.message}`);
        
        try {
          const basicUrl = `${this.nbaBaseURL}/SchedulesBasic/${season}`;
          data = await this.makeRequest(basicUrl, this.nbaApiKey, `NBA SchedulesBasic ${season}`);
        } catch (basicError) {
          logger.warn(` SchedulesBasic failed, trying Schedules: ${basicError.message}`);
          
          try {
            const scheduleUrl = `${this.nbaBaseURL}/Schedules/${season}`;
            data = await this.makeRequest(scheduleUrl, this.nbaApiKey, `NBA Schedules ${season}`);
          } catch (scheduleError) {
            logger.warn(` Regular Schedules failed: ${scheduleError.message}`);
            throw new Error('All NBA endpoints failed');
          }
        }
      }
      
      if (data && Array.isArray(data) && data.length > 0) {
        logger.success(` Raw NBA data received: ${data.length} total games`);
        
        const teamGames = data.filter(game => {
          const homeMatch = game.HomeTeam === teamKey || 
                           game.HomeTeamName?.includes(teamKey) ||
                           game.home_team === teamKey;
          const awayMatch = game.AwayTeam === teamKey || 
                           game.AwayTeamName?.includes(teamKey) ||
                           game.away_team === teamKey;
          
          return homeMatch || awayMatch;
        });
        
        logger.debug(`🎯 Filtered games for ${teamKey}: ${teamGames.length} games`);
        
        if (teamGames.length > 0) {
          logger.success(` Found ${teamGames.length} NBA games for ${teamKey}`);
          const parsedGames = this.parseNBAGames(teamGames, teamKey, season);
          logger.debug(` Parsed ${parsedGames.length} NBA games`);
          return parsedGames;
        }
      }
      
    } catch (error) {
      logger.error(`❌ NBA Schedule failed for ${teamKey}:`, error);
    }
    
    logger.debug(` Generating fallback NBA schedule for ${teamKey}`);
    return this.generateFallbackNBASchedule(teamKey, season);
  }

  parseNBAGames(games, teamKey, season = getBasketballHockeySeasonYear()) {
    logger.debug(` Parsing ${games.length} NBA games for ${teamKey}`);
    
    return games.map((game, index) => {
      try {
        const homeTeam = game.HomeTeam || game.home_team || game.HomeTeamName;
        const awayTeam = game.AwayTeam || game.away_team || game.AwayTeamName;
        
        const isHomeGame = homeTeam === teamKey;
        const opponent = isHomeGame ? awayTeam : homeTeam;
        
        let gameDate;
        if (game.DateTime) {
          gameDate = new Date(game.DateTime);
        } else if (game.Date) {
          gameDate = new Date(game.Date);
        } else {
          const gameNum = game.GameNumber || (index + 1);
          gameDate = new Date(season, 9, gameNum * 2); // October start
        }
        
        const isCompleted = game.Status === 'Final' || 
                            game.IsClosed === true || 
                            game.status === 'completed';
        
        let gameResult = null;
        if (isCompleted && game.HomeScore !== null && game.AwayScore !== null) {
          const teamScore = isHomeGame ? game.HomeScore : game.AwayScore;
          const opponentScore = isHomeGame ? game.AwayScore : game.HomeScore;
          
          if (teamScore > opponentScore) gameResult = 'W';
          else if (teamScore < opponentScore) gameResult = 'L';
          else gameResult = 'T';
        }

        return {
          id: String(game.GameID || game.id || `nba_${teamKey}_${index}_${game.GameNumber || index + 1}`),
          date: gameDate,
          opponent: opponent,
          opponentId: opponent,
          isHomeGame: isHomeGame,
          venue: game.StadiumDetails?.Name || (isHomeGame ? 'Home Arena' : `@${opponent}`),
          status: game.Status || game.status || 'Scheduled',
          isCompleted: isCompleted,
          homeScore: game.HomeScore || null,
          awayScore: game.AwayScore || null,
          gameResult: gameResult,
          week: game.GameNumber || (index + 1),
          time: gameDate,
          broadcast: game.Channel || null,
          season: game.Season || season,
          dataSource: 'SportsData.io NBA'
        };
        
      } catch (error) {
        logger.error(` Failed to parse NBA game ${index}:`, error);
        return {
          id: `nba_${teamKey}_${index}_fallback`,
          date: new Date(season, 9, (index + 1) * 2),
          opponent: 'TBD',
          opponentId: 'TBD',
          isHomeGame: index % 2 === 0,
          venue: 'TBD',
          status: 'Scheduled',
          isCompleted: false,
          homeScore: null,
          awayScore: null,
          gameResult: null,
          week: index + 1,
          time: new Date(season, 9, (index + 1) * 2),
          broadcast: null,
          season: season,
          dataSource: 'NBA Fallback'
        };
      }
    });
  }

  generateFallbackNBASchedule(teamKey, season) {
    logger.debug(` Generating fallback NBA schedule for ${teamKey}`);
    
    const fallbackGames = [];
    
    for (let game = 1; game <= 82; game++) {
      const gameDate = new Date(season, 9, game * 2); // October start, games every 2 days
      
      fallbackGames.push({
        id: `${teamKey}_game_${game}_${season}`,
        date: gameDate,
        opponent: 'TBD',
        opponentId: 'TBD',
        isHomeGame: game % 2 === 0,
        venue: game % 2 === 0 ? 'Home Arena' : 'Away',
        status: 'Scheduled',
        isCompleted: false,
        homeScore: null,
        awayScore: null,
        gameResult: null,
        week: game,
        time: gameDate,
        broadcast: null,
        season: season,
        dataSource: 'SportsData.io NBA Fallback',
        isFallback: true
      });
    }
    
    logger.success(` Generated ${fallbackGames.length} fallback NBA games`);
    return fallbackGames;
  }

  // =====================================================
  // 🔥 NEW: NHL METHODS
  // =====================================================

  async getNHLTeams() {
    const url = `${this.nhlBaseURL}/Teams`;
    return await this.makeRequest(url, this.nhlApiKey, 'NHL Teams');
  }

  // Map NHL team names to SportsData.io abbreviations
  getNHLTeamKeyFromName(teamName) {
    const nhlMapping = {
      'Anaheim Ducks': 'ANA',
      'Arizona Coyotes': 'ARI',
      'Boston Bruins': 'BOS',
      'Buffalo Sabres': 'BUF',
      'Calgary Flames': 'CGY',
      'Carolina Hurricanes': 'CAR',
      'Chicago Blackhawks': 'CHI',
      'Colorado Avalanche': 'COL',
      'Columbus Blue Jackets': 'CBJ',
      'Dallas Stars': 'DAL',
      'Detroit Red Wings': 'DET',
      'Edmonton Oilers': 'EDM',
      'Florida Panthers': 'FLA',
      'Los Angeles Kings': 'LAK',
      'Minnesota Wild': 'MIN',
      'Montreal Canadiens': 'MTL',
      'Nashville Predators': 'NSH',
      'New Jersey Devils': 'NJD',
      'New York Islanders': 'NYI',
      'New York Rangers': 'NYR',
      'Ottawa Senators': 'OTT',
      'Philadelphia Flyers': 'PHI',
      'Pittsburgh Penguins': 'PIT',
      'San Jose Sharks': 'SJS',
      'Seattle Kraken': 'SEA',
      'St. Louis Blues': 'STL',
      'Tampa Bay Lightning': 'TB',
      'Toronto Maple Leafs': 'TOR',
      'Vancouver Canucks': 'VAN',
      'Vegas Golden Knights': 'VGK',
      'Washington Capitals': 'WSH',
      'Winnipeg Jets': 'WPG'
    };
    
    return nhlMapping[teamName] || teamName;
  }

  async getNHLSchedule(teamName, season = getBasketballHockeySeasonYear()) {
    const teamKey = this.getNHLTeamKeyFromName(teamName);
    logger.info(`🏒 Getting NHL schedule for ${teamName} (${teamKey})`);
    
    try {
      let data = null;
      
      // Try team-specific endpoint first (most reliable)
      try {
        const teamUrl = `${this.nhlBaseURL}/Games/${season}/${teamKey}`;
        logger.debug(`🔗 Trying team-specific: ${teamUrl}`);
        data = await this.makeRequest(teamUrl, this.nhlApiKey, `NHL Team Games ${teamKey}`);
      } catch (teamError) {
        logger.warn(` Team-specific failed, trying SchedulesBasic: ${teamError.message}`);
        
        try {
          const basicUrl = `${this.nhlBaseURL}/SchedulesBasic/${season}`;
          data = await this.makeRequest(basicUrl, this.nhlApiKey, `NHL SchedulesBasic ${season}`);
        } catch (basicError) {
          logger.warn(` SchedulesBasic failed, trying Schedules: ${basicError.message}`);
          
          try {
            const scheduleUrl = `${this.nhlBaseURL}/Schedules/${season}`;
            data = await this.makeRequest(scheduleUrl, this.nhlApiKey, `NHL Schedules ${season}`);
          } catch (scheduleError) {
            logger.warn(` Regular Schedules failed: ${scheduleError.message}`);
            throw new Error('All NHL endpoints failed');
          }
        }
      }
      
      if (data && Array.isArray(data) && data.length > 0) {
        logger.success(` Raw NHL data received: ${data.length} total games`);
        
        const teamGames = data.filter(game => {
          const homeMatch = game.HomeTeam === teamKey || 
                           game.HomeTeamName?.includes(teamKey) ||
                           game.home_team === teamKey;
          const awayMatch = game.AwayTeam === teamKey || 
                           game.AwayTeamName?.includes(teamKey) ||
                           game.away_team === teamKey;
          
          return homeMatch || awayMatch;
        });
        
        logger.debug(`🎯 Filtered games for ${teamKey}: ${teamGames.length} games`);
        
        if (teamGames.length > 0) {
          logger.success(` Found ${teamGames.length} NHL games for ${teamKey}`);
          const parsedGames = this.parseNHLGames(teamGames, teamKey, season);
          logger.debug(` Parsed ${parsedGames.length} NHL games`);
          return parsedGames;
        }
      }
      
    } catch (error) {
      logger.error(`❌ NHL Schedule failed for ${teamKey}:`, error);
    }
    
    logger.debug(` Generating fallback NHL schedule for ${teamKey}`);
    return this.generateFallbackNHLSchedule(teamKey, season);
  }

  parseNHLGames(games, teamKey, season = getBasketballHockeySeasonYear()) {
    logger.debug(` Parsing ${games.length} NHL games for ${teamKey}`);
    
    return games.map((game, index) => {
      try {
        const homeTeam = game.HomeTeam || game.home_team || game.HomeTeamName;
        const awayTeam = game.AwayTeam || game.away_team || game.AwayTeamName;
        
        const isHomeGame = homeTeam === teamKey;
        const opponent = isHomeGame ? awayTeam : homeTeam;
        
        let gameDate;
        if (game.DateTime) {
          gameDate = new Date(game.DateTime);
        } else if (game.Date) {
          gameDate = new Date(game.Date);
        } else {
          const gameNum = game.GameNumber || (index + 1);
          gameDate = new Date(season, 9, gameNum * 2); // October start
        }
        
        const isCompleted = game.Status === 'Final' || 
                            game.IsClosed === true || 
                            game.status === 'completed';
        
        let gameResult = null;
        if (isCompleted && game.HomeScore !== null && game.AwayScore !== null) {
          const teamScore = isHomeGame ? game.HomeScore : game.AwayScore;
          const opponentScore = isHomeGame ? game.AwayScore : game.HomeScore;
          
          if (teamScore > opponentScore) gameResult = 'W';
          else if (teamScore < opponentScore) gameResult = 'L';
          else gameResult = 'T';
        }

        return {
          id: String(game.GameID || game.id || `nhl_${teamKey}_${index}_${game.GameNumber || index + 1}`),
          date: gameDate,
          opponent: opponent,
          opponentId: opponent,
          isHomeGame: isHomeGame,
          venue: game.StadiumDetails?.Name || (isHomeGame ? 'Home Arena' : `@${opponent}`),
          status: game.Status || game.status || 'Scheduled',
          isCompleted: isCompleted,
          homeScore: game.HomeScore || null,
          awayScore: game.AwayScore || null,
          gameResult: gameResult,
          week: game.GameNumber || (index + 1),
          time: gameDate,
          broadcast: game.Channel || null,
          season: game.Season || season,
          dataSource: 'SportsData.io NHL'
        };
        
      } catch (error) {
        logger.error(` Failed to parse NHL game ${index}:`, error);
        return {
          id: `nhl_${teamKey}_${index}_fallback`,
          date: new Date(season, 9, (index + 1) * 2),
          opponent: 'TBD',
          opponentId: 'TBD',
          isHomeGame: index % 2 === 0,
          venue: 'TBD',
          status: 'Scheduled',
          isCompleted: false,
          homeScore: null,
          awayScore: null,
          gameResult: null,
          week: index + 1,
          time: new Date(season, 9, (index + 1) * 2),
          broadcast: null,
          season: season,
          dataSource: 'NHL Fallback'
        };
      }
    });
  }

  generateFallbackNHLSchedule(teamKey, season) {
    logger.debug(` Generating fallback NHL schedule for ${teamKey}`);
    
    const fallbackGames = [];
    
    for (let game = 1; game <= 82; game++) {
      const gameDate = new Date(season, 9, game * 2); // October start, games every 2 days
      
      fallbackGames.push({
        id: `${teamKey}_game_${game}_${season}`,
        date: gameDate,
        opponent: 'TBD',
        opponentId: 'TBD',
        isHomeGame: game % 2 === 0,
        venue: game % 2 === 0 ? 'Home Arena' : 'Away',
        status: 'Scheduled',
        isCompleted: false,
        homeScore: null,
        awayScore: null,
        gameResult: null,
        week: game,
        time: gameDate,
        broadcast: null,
        season: season,
        dataSource: 'SportsData.io NHL Fallback',
        isFallback: true
      });
    }
    
    logger.success(` Generated ${fallbackGames.length} fallback NHL games`);
    return fallbackGames;
  }

  // =====================================================
  // 🔥 ENHANCED: UNIFIED TEAM SCHEDULE METHOD
  // =====================================================

  async getTeamSchedule(teamName, league, season) {
    const resolvedSeason = season ?? getSeasonYearForLeague(league);
    logger.info(`📅 Getting ${league} schedule for ${teamName}`);
    
    try {
      switch (league.toLowerCase()) {
        case 'nfl':
          return await this.getNFLSchedule(teamName, resolvedSeason);
        case 'college':
        case 'cfb':
          return await this.getCollegeSchedule(teamName, resolvedSeason);
        case 'mlb':
          return await this.getMLBSchedule(teamName, resolvedSeason);
        case 'nba':
          return await this.getNBASchedule(teamName, resolvedSeason);
        case 'nhl':
          return await this.getNHLSchedule(teamName, resolvedSeason);
        default:
          logger.warn(` Unknown league: ${league}`);
          return [];
      }
    } catch (error) {
      logger.error(`❌ Failed to get ${league} schedule for ${teamName}:`, error);
      return [];
    }
  }

  async testNFL() {
    console.group('🏈 NFL API Test');
    try {
      const teams = await this.getNFLTeams();
      logger.success(` NFL Teams: ${teams.length} found`);
      
      const schedule = await this.getNFLSchedule('GB');
      logger.success(` Packers Schedule: ${schedule.length} games`);
      
      const roster = await this.getNFLRoster('GB');
      logger.success(` Packers Roster: ${roster.length} players`);
      
      return { teams: teams.length, schedule: schedule.length, roster: roster.length };
    } catch (error) {
      console.error('❌ NFL Test failed:', error);
      return { error: error.message };
    } finally {
      console.groupEnd();
    }
  }

  async testCollege() {
    console.group('🏫 College API Test');
    try {
      const teams = await this.getCollegeTeams();
      logger.success(` College Teams: ${teams.length} found`);
      
      const schedule = await this.getCollegeSchedule('WISC');
      logger.success(` Wisconsin Schedule: ${schedule.length} games`);
      
      const roster = await this.getCollegeRoster('WISC');
      logger.success(` Wisconsin Roster: ${roster.length} players`);
      
      return { teams: teams.length, schedule: schedule.length, roster: roster.length };
    } catch (error) {
      console.error('❌ College Test failed:', error);
      return { error: error.message };
    } finally {
      console.groupEnd();
    }
  }

  async testMLB() {
    console.group('⚾ MLB API Test');
    try {
      const teams = await this.getMLBTeams();
      logger.success(` MLB Teams: ${teams.length} found`);
      
      const schedule = await this.getMLBSchedule('New York Yankees');
      logger.success(` Yankees Schedule: ${schedule.length} games`);
      
      return { teams: teams.length, schedule: schedule.length };
    } catch (error) {
      console.error('❌ MLB Test failed:', error);
      return { error: error.message };
    } finally {
      console.groupEnd();
    }
  }

  async testNBA() {
    console.group('🏀 NBA API Test');
    try {
      const teams = await this.getNBATeams();
      logger.success(` NBA Teams: ${teams.length} found`);
      
      const schedule = await this.getNBASchedule('Los Angeles Lakers');
      logger.success(` Lakers Schedule: ${schedule.length} games`);
      
      return { teams: teams.length, schedule: schedule.length };
    } catch (error) {
      console.error('❌ NBA Test failed:', error);
      return { error: error.message };
    } finally {
      console.groupEnd();
    }
  }

  async testNHL() {
    console.group('🏒 NHL API Test');
    try {
      const teams = await this.getNHLTeams();
      logger.success(` NHL Teams: ${teams.length} found`);
      
      const schedule = await this.getNHLSchedule('Boston Bruins');
      logger.success(` Bruins Schedule: ${schedule.length} games`);
      
      return { teams: teams.length, schedule: schedule.length };
    } catch (error) {
      console.error('❌ NHL Test failed:', error);
      return { error: error.message };
    } finally {
      console.groupEnd();
    }
  }

  async runFullTest() {
    logger.navigation('🚀 Running Full SportsData.io Test Suite');
    
    const results = {
      nfl: await this.testNFL(),
      college: await this.testCollege(),
      mlb: await this.testMLB(),
      nba: await this.testNBA(),
      nhl: await this.testNHL()
    };
    
    logger.debug('📊 Test Results:', results);
    return results;
  }
}

// 🔥 NEW: Expose debug functions to window
if (typeof window !== 'undefined') {
  const sportsDataService = new SportsDataService();
  window.sportsDataService = sportsDataService;
  
  // Expose debug functions
  window.debugNFLSchedule = async (teamKey = 'GB') => {
    try {
      return await sportsDataService.debugNFLSchedule(teamKey);
    } catch (error) {
      console.error('❌ SportsDataService error:', error);
      return { error: error.message };
    }
  };
  
  window.testNFLSchedule = () => window.debugNFLSchedule('GB');
  
  // New league debug functions
  window.debugMLBSchedule = async (teamName = 'New York Yankees') => {
    try {
      return await sportsDataService.getMLBSchedule(teamName);
    } catch (error) {
      console.error('❌ MLB Schedule error:', error);
      return { error: error.message };
    }
  };
  
  window.debugNBASchedule = async (teamName = 'Los Angeles Lakers') => {
    try {
      return await sportsDataService.getNBASchedule(teamName);
    } catch (error) {
      console.error('❌ NBA Schedule error:', error);
      return { error: error.message };
    }
  };
  
  window.debugNHLSchedule = async (teamName = 'Boston Bruins') => {
    try {
      return await sportsDataService.getNHLSchedule(teamName);
    } catch (error) {
      console.error('❌ NHL Schedule error:', error);
      return { error: error.message };
    }
  };
  
  window.testAllLeagues = () => sportsDataService.runFullTest();
  
  // API Key diagnostic function
  window.diagnoseAPIKey = async () => {
    console.group('🔍 API Key Diagnostic');
    try {
      const results = {};
      
      // Test basic endpoints for each league
      const leagues = [
        { name: 'NFL', url: 'https://api.sportsdata.io/v3/nfl/scores/json/Teams', key: 'nfl' },
        { name: 'MLB', url: 'https://api.sportsdata.io/v3/mlb/scores/json/Teams', key: 'mlb' },
        { name: 'NBA', url: 'https://api.sportsdata.io/v3/nba/scores/json/Teams', key: 'nba' },
        { name: 'NHL', url: 'https://api.sportsdata.io/v3/nhl/scores/json/Teams', key: 'nhl' }
      ];
      
      for (const league of leagues) {
        try {
          const proxied = `/api/sportsdata-proxy?league=${league.key}&url=${encodeURIComponent(league.url)}`;
          const response = await fetch(proxied);
          const data = await response.json();
          
          results[league.name] = {
            status: response.status,
            success: response.ok,
            dataLength: Array.isArray(data) ? data.length : 'Not an array',
            error: response.ok ? null : data
          };
          
          console.log(`✅ ${league.name}: ${response.status} - ${Array.isArray(data) ? data.length : 'Error'} items`);
        } catch (error) {
          results[league.name] = {
            status: 'Error',
            success: false,
            error: error.message
          };
          console.log(`❌ ${league.name}: ${error.message}`);
        }
      }
      
      console.log('📊 Diagnostic Results:', results);
      return results;
    } catch (error) {
      console.error('❌ Diagnostic failed:', error);
      return { error: error.message };
    } finally {
      console.groupEnd();
    }
  };
  
  logger.debug('🧪 Multi-League Debug commands available:');
  logger.debug('  window.debugNFLSchedule("GB") - Debug Green Bay schedule');
  logger.debug('  window.debugMLBSchedule("New York Yankees") - Debug Yankees schedule');
  logger.debug('  window.debugNBASchedule("Los Angeles Lakers") - Debug Lakers schedule');
  logger.debug('  window.debugNHLSchedule("Boston Bruins") - Debug Bruins schedule');
  logger.debug('  window.testAllLeagues() - Test all leagues');
  logger.debug('  window.diagnoseAPIKey() - Diagnose API key access');
}

export default SportsDataService;